import { useState, useCallback } from 'react';
import * as partnerApi from '../../api/partnerApi';

// ---------------------------------------------------------------------------
// Category labels for section headers
// ---------------------------------------------------------------------------
const CATEGORY_LABELS = {
  platform: 'EVENT',
  orchestrator: 'PIPELINE',
  extraction: 'AI EXTRACTION',
  validation: 'VALIDATION',
  result: 'RESULT',
  issues: 'ISSUES',
};

// ---------------------------------------------------------------------------
// Status icon SVGs (inline for zero dependencies)
// ---------------------------------------------------------------------------
function CheckIcon() {
  return (
    <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg className="w-3.5 h-3.5 text-blue-600 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={3} strokeDasharray="50 20" strokeLinecap="round" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg className="w-3.5 h-3.5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg className="w-3.5 h-3.5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function WrenchIcon() {
  return (
    <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Flag icon by type (for quality section), colored by severity
// ---------------------------------------------------------------------------
function FlagIcon({ type, severity }) {
  const severityColors = {
    error: 'text-red-500',
    warning: 'text-amber-500',
    info: 'text-blue-500',
  };
  const cls = severityColors[severity] || 'text-gray-400';

  const paths = {
    hallucination: 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z',
    mismatch: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z',
    missing: 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z',
    duplicate: 'M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75',
    new_partner: 'M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z',
    address_mismatch: 'M15 10.5a3 3 0 11-6 0 3 3 0 016 0z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z',
    missing_po_number: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9zm3.75 11.625a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
    price_anomaly: 'M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  };
  const d = paths[type] || 'M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z';

  return (
    <svg className={`w-3.5 h-3.5 ${cls}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Severity-based color config for issue circles
// ---------------------------------------------------------------------------
const SEVERITY_COLORS = {
  error: { bg: 'bg-red-50', ring: 'ring-red-200' },
  warning: { bg: 'bg-amber-50', ring: 'ring-amber-200' },
  info: { bg: 'bg-blue-50', ring: 'ring-blue-200' },
};

// ---------------------------------------------------------------------------
// Source labels for the small source badge on each issue
// ---------------------------------------------------------------------------
const SOURCE_LABELS = {
  evaluation: 'Extraction Quality',
  duplicate_check: 'Duplicates',
  master_data: 'Master Data',
  structure: 'Structure',
  pricing: 'Pricing',
};

// ---------------------------------------------------------------------------
// Status config: colors, icons, ring styles
// ---------------------------------------------------------------------------
const STATUS_CONFIG = {
  complete: {
    bg: 'bg-emerald-50',
    ring: 'ring-emerald-200',
    icon: CheckIcon,
    lineBg: 'bg-emerald-200',
  },
  active: {
    bg: 'bg-blue-50',
    ring: 'ring-blue-200',
    icon: SpinnerIcon,
    lineBg: 'bg-blue-200',
  },
  pending: {
    bg: 'bg-gray-50',
    ring: 'ring-gray-200',
    icon: null,
    lineBg: 'bg-gray-100',
  },
  warning: {
    bg: 'bg-amber-50',
    ring: 'ring-amber-200',
    icon: WarningIcon,
    lineBg: 'bg-amber-200',
  },
  error: {
    bg: 'bg-red-50',
    ring: 'ring-red-200',
    icon: ErrorIcon,
    lineBg: 'bg-red-200',
  },
};

// ---------------------------------------------------------------------------
// Timestamp formatter
// ---------------------------------------------------------------------------
function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

// ---------------------------------------------------------------------------
// Timeline step component
// ---------------------------------------------------------------------------
function TimelineStep({ step, isLast, index }) {
  const [expanded, setExpanded] = useState(false);
  const config = STATUS_CONFIG[step.status] || STATUS_CONFIG.pending;
  const IconComponent = config.icon;
  const hasWarnings = Array.isArray(step.warnings) && step.warnings.length > 0;

  return (
    <div
      className="flex gap-3 relative"
      style={{ animation: `fadeSlideIn 0.35s ease both`, animationDelay: `${index * 0.06}s` }}
    >
      {/* Left column: status circle + connecting line */}
      <div className="flex flex-col items-center" style={{ width: '28px', minWidth: '28px' }}>
        <div className={`w-7 h-7 rounded-full ring-2 ${config.ring} ${config.bg} flex items-center justify-center flex-shrink-0`}>
          {IconComponent ? <IconComponent /> : (
            <div className="w-2 h-2 rounded-full bg-gray-300" />
          )}
        </div>
        {!isLast && (
          <div className={`w-0.5 flex-1 mt-1 ${config.lineBg}`} style={{ minHeight: '16px' }} />
        )}
      </div>

      {/* Right column: content */}
      <div className="flex-1 pb-4 min-w-0">
        {/* Top row: label, badges, timestamp */}
        <div
          className={`flex items-center gap-2 flex-wrap ${hasWarnings ? 'cursor-pointer' : ''}`}
          onClick={hasWarnings ? () => setExpanded(!expanded) : undefined}
        >
          <span className="text-sm font-semibold text-gray-900">{step.label}</span>

          {step.isToolCall && step.toolName && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600" style={{ fontSize: '10px' }}>
              <WrenchIcon />
              <span className="font-mono">{step.toolName}</span>
            </span>
          )}

          {hasWarnings && (
            <svg
              className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform duration-150 ${expanded ? '' : '-rotate-90'}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          )}

          {step.duration && (
            <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-mono" style={{ fontSize: '10px' }}>
              {step.duration}
            </span>
          )}

          <span className="ml-auto font-mono text-gray-300 flex-shrink-0" style={{ fontSize: '10px' }}>
            {formatTime(step.timestamp)}
          </span>
        </div>

        {/* Detail text */}
        {step.status === 'active' ? (
          <p className="text-xs text-blue-500 mt-0.5 animate-pulse">Processing...</p>
        ) : step.detail ? (
          <p className="text-xs text-gray-500 mt-0.5">{step.detail}</p>
        ) : null}

        {/* Expandable warnings list */}
        {hasWarnings && expanded && (
          <ul className="mt-1 space-y-0.5">
            {step.warnings.map((w, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-amber-700">
                <span className="flex-shrink-0 mt-0.5"><WarningIcon /></span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Category section header
// ---------------------------------------------------------------------------
function CategoryHeader({ category }) {
  return (
    <div className="flex items-center gap-2 pt-3 pb-1.5">
      <span className="text-[10px] font-semibold tracking-widest text-gray-400 uppercase">
        {CATEGORY_LABELS[category] || category}
      </span>
      <div className="flex-1 h-px bg-gray-100" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Quality issues — standalone named export for the Issues tab
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Text formatting helpers
// ---------------------------------------------------------------------------

// PARTY_TYPE_LABELS — human-readable names for raw party type constants
const PARTY_TYPE_LABELS = {
  VENDOR: 'Vendor',
  BUYER: 'Buyer',
  CONSIGNEE: 'Consignee',
  SHIPPER: 'Shipper',
  BOOKING_AGENT: 'Booking Agent',
  NOTIFY_PARTY: 'Notify Party',
  CARRIER: 'Carrier',
  BENEFICIAL_CARGO_OWNER: 'Beneficial Cargo Owner',
};

// Replace raw SCREAMING_SNAKE party types with readable labels in a string
function formatPartyTypes(text) {
  if (!text) return text;
  return Object.entries(PARTY_TYPE_LABELS).reduce(
    (str, [raw, label]) => str.replaceAll(raw, label),
    text,
  );
}

// Strip MongoDB hex ObjectIds (24-char hex strings) from visible text
function stripObjectIds(text) {
  if (!text) return text;
  // Match standalone 24-char hex strings, optionally preceded by "id: " or "(id: " etc.
  return text.replace(/\(?\s*id:?\s*[0-9a-f]{24}\s*\)?,?\s*/gi, '').trim();
}

// Parse a detail string into an array of concern strings.
// Splits on numbered markers like "(1)", "(2)" or "1)", "2)".
// Falls back to the full string as a single item.
function parseDetailConcerns(detail) {
  if (!detail) return [];
  // Try splitting on (1)...(2)...(3) pattern
  const numbered = detail.split(/\s*\(\d+\)\s*/).filter(Boolean);
  if (numbered.length > 1) return numbered.map(s => formatPartyTypes(stripObjectIds(s.trim())));
  // Try splitting on "1) ... 2) ..." pattern (no parens)
  const bare = detail.split(/\s*\d+\)\s*/).filter(Boolean);
  if (bare.length > 1) return bare.map(s => formatPartyTypes(stripObjectIds(s.trim())));
  // Single concern — clean it up
  return [formatPartyTypes(stripObjectIds(detail))];
}

// Human-readable issue type labels
const TYPE_LABELS = {
  new_partner: 'New Partner',
  address_mismatch: 'Address Mismatch',
  new_party_type: 'New Role',
  new_facility: 'New Facility',
  possible_alias: 'Possible Alias',
  possible_subdivision: 'Possible Subdivision',
  hallucination: 'Hallucination',
  mismatch: 'Data Mismatch',
  missing: 'Missing Data',
  duplicate: 'Duplicate',
  missing_po_number: 'Missing PO Number',
  price_anomaly: 'Price Anomaly',
  observation: 'Observation',
};

// Action-type icons (small, contextual)
const ACTION_ICONS = {
  add_address: 'M15 10.5a3 3 0 11-6 0 3 3 0 016 0z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z',
  add_alias: 'M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z',
  add_subdivision_alias: 'M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z',
  create_partner: 'M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z',
  create_subdivision: 'M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21',
  activate_partner: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  adopt_partner: 'M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5',
};

// ---------------------------------------------------------------------------
// Map of action types to API call functions.
// Each function takes an action's params object and returns a promise.
// ---------------------------------------------------------------------------
const ACTION_HANDLERS = {
  add_address: (params) => {
    // partyTypes may be at top level or nested inside address (prompt puts it inside address)
    const { partyTypes: addrPartyTypes, ...addrFields } = params.address || {};
    const partyTypes = params.partyTypes || addrPartyTypes || [];
    const address = { ...addrFields, partyTypes };
    return params.subdivisionId
      ? partnerApi.addSubdivisionAddress(params.partnerId, params.subdivisionId, address)
      : partnerApi.addPartnerAddress(params.partnerId, address);
  },
  add_alias: (params) =>
    partnerApi.addPartnerAlias(params.partnerId, params.alias),
  add_subdivision_alias: (params) =>
    partnerApi.addSubdivisionAlias(params.partnerId, params.subdivisionId, params.alias),
  create_partner: (params) =>
    partnerApi.createPartner({
      name: params.name,
      address: params.address,
      partyTypes: params.partyType ? [params.partyType] : [],
    }),
  create_subdivision: (params) =>
    partnerApi.addSubdivision(params.partnerId, {
      name: params.name,
      addresses: params.address ? [{
        ...params.address,
        partyTypes: params.partyType ? [params.partyType] : [],
      }] : [],
    }),
  activate_partner: (params) =>
    partnerApi.updatePartnerStatus(params.partnerId, 'active'),
  adopt_partner: (params) =>
    partnerApi.adoptPartner(params.partnerId, params.childPartnerId),
};

// ---------------------------------------------------------------------------
// Single action button with loading/success/error states
// ---------------------------------------------------------------------------
function IssueActionButton({ action, onExecuted }) {
  const [state, setState] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState(null);

  const handleClick = useCallback(async () => {
    const handler = ACTION_HANDLERS[action.action];
    if (!handler) return;

    setState('loading');
    setErrorMsg(null);
    try {
      await handler(action.params || {});
      setState('success');
      onExecuted?.(action);
    } catch (err) {
      setState('error');
      setErrorMsg(err.response?.data?.error || err.message || 'Action failed');
    }
  }, [action, onExecuted]);

  const iconPath = ACTION_ICONS[action.action];
  const cleanLabel = formatPartyTypes(action.label);

  if (state === 'success') {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200">
        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        <span>{cleanLabel}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <button
        onClick={handleClick}
        disabled={state === 'loading'}
        className={`group flex items-center gap-1.5 w-full text-left px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-all duration-150
          ${state === 'error'
            ? 'border-red-200 text-red-700 bg-red-50 hover:bg-red-100 hover:border-red-300'
            : 'border-gray-200 text-gray-700 bg-white hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700'
          }
          disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {state === 'loading' ? (
          <svg className="w-3.5 h-3.5 flex-shrink-0 animate-spin text-blue-500" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={3} strokeDasharray="50 20" strokeLinecap="round" />
          </svg>
        ) : iconPath ? (
          <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        )}
        <span>{cleanLabel}</span>
      </button>
      {state === 'error' && errorMsg && (
        <span className="text-[10px] text-red-500 mt-1 px-2.5">{errorMsg}</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Severity-based card styling
// ---------------------------------------------------------------------------
const SEVERITY_CARD_STYLES = {
  error: {
    border: 'border-red-200',
    bg: 'bg-red-50/40',
    iconBg: 'bg-red-100',
    iconRing: 'ring-red-200',
    accentBar: 'bg-red-400',
    badge: 'bg-red-100 text-red-700',
  },
  warning: {
    border: 'border-amber-200',
    bg: 'bg-amber-50/40',
    iconBg: 'bg-amber-100',
    iconRing: 'ring-amber-200',
    accentBar: 'bg-amber-400',
    badge: 'bg-amber-100 text-amber-700',
  },
  info: {
    border: 'border-blue-200',
    bg: 'bg-blue-50/30',
    iconBg: 'bg-blue-100',
    iconRing: 'ring-blue-200',
    accentBar: 'bg-blue-400',
    badge: 'bg-blue-100 text-blue-700',
  },
};

const DEFAULT_CARD_STYLE = {
  border: 'border-gray-200',
  bg: 'bg-gray-50/40',
  iconBg: 'bg-gray-100',
  iconRing: 'ring-gray-200',
  accentBar: 'bg-gray-400',
  badge: 'bg-gray-100 text-gray-700',
};

// ---------------------------------------------------------------------------
// Single issue card
// ---------------------------------------------------------------------------
function IssueCard({ issue, onIssueResolved }) {
  const [expanded, setExpanded] = useState(true);
  const style = SEVERITY_CARD_STYLES[issue.severity] || DEFAULT_CARD_STYLE;
  const typeLabel = TYPE_LABELS[issue.type] || (issue.type || '').replace(/_/g, ' ');
  const sourceLabel = SOURCE_LABELS[issue.source] || issue.source;
  const concerns = parseDetailConcerns(issue.detail);
  const actions = issue.suggestedActions || [];

  return (
    <div
      className={`rounded-lg border ${style.border} ${style.bg} overflow-hidden transition-all duration-200`}
      style={{ animation: 'fadeSlideIn 0.3s ease both' }}
    >
      {/* Card header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-white/40 transition-colors"
      >
        <div className={`w-7 h-7 rounded-full ring-1 ${style.iconRing} ${style.iconBg} flex items-center justify-center flex-shrink-0`}>
          <FlagIcon type={issue.type} severity={issue.severity} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[13px] font-semibold text-gray-900">{typeLabel}</span>
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${style.badge}`}>
              {sourceLabel}
            </span>
          </div>
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${expanded ? '' : '-rotate-90'}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expandable body */}
      {expanded && (
        <div className="px-3 pb-3">
          {/* Concerns list */}
          {concerns.length === 1 ? (
            <p className="text-xs text-gray-600 leading-relaxed mb-2 ml-9">
              {concerns[0]}
            </p>
          ) : (
            <ul className="space-y-1 mb-2 ml-9">
              {concerns.map((concern, i) => (
                <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600 leading-relaxed">
                  <span className="flex-shrink-0 w-4 h-4 rounded-full bg-gray-200/60 text-gray-500 flex items-center justify-center mt-px" style={{ fontSize: '9px', fontWeight: 600 }}>
                    {i + 1}
                  </span>
                  <span>{concern}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Actions */}
          {actions.length > 0 && (
            <div className="ml-9 space-y-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Actions</span>
              <div className="flex flex-col gap-1">
                {actions.map((action, j) => (
                  <IssueActionButton
                    key={j}
                    action={action}
                    onExecuted={() => onIssueResolved?.(issue._origIndex)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function QualityIssues({ issues, onIssueResolved }) {
  if (issues == null) return null;

  // Tag each issue with its original array index (needed for backend resolution),
  // then filter out already-resolved issues so they don't show action buttons again.
  const activeIssues = issues
    .map((issue, idx) => ({ ...issue, _origIndex: idx }))
    .filter(issue => !issue.resolvedAt);

  return (
    <div>
      {activeIssues.length === 0 ? (
        <div className="flex items-center gap-2.5 py-3">
          <div className="flex items-center justify-center" style={{ width: '28px', minWidth: '28px' }}>
            <div className="w-7 h-7 rounded-full ring-2 ring-emerald-200 bg-emerald-50 flex items-center justify-center">
              <CheckIcon />
            </div>
          </div>
          <span className="text-sm text-gray-500">No issues detected</span>
        </div>
      ) : (
        <div className="space-y-2.5">
          {activeIssues.map((issue, i) => (
            <IssueCard
              key={i}
              issue={issue}
              onIssueResolved={onIssueResolved}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function ExtractionPipelineTimeline({ steps = [] }) {
  return (
    <div>
      {/* Inline keyframe animation */}
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {steps.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">No processing activity yet.</p>
      ) : (
        steps.map((step, i) => {
          const prevCategory = i > 0 ? steps[i - 1].category : null;
          const showHeader = step.category !== prevCategory;

          return (
            <div key={`${step.stepId}-${i}`}>
              {showHeader && <CategoryHeader category={step.category} />}
              <TimelineStep
                step={step}
                isLast={i === steps.length - 1}
                index={i}
              />
            </div>
          );
        })
      )}
    </div>
  );
}
